import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchTextSign, fetchAnnotations } from "@/features/text-sign/api";
import {
  ArrowDown,
  ArrowUp,
  Copy,
  FileSignature,
  FileText,
  MapPin,
  Paperclip,
  Loader2,
  Save,
  Send,
  Trash2,
  UserCheck,
  UserPlus,
  X,
} from "lucide-react";
import { AnnotationModal } from "./AnnotationModal";
import { PageHeader } from "@/components/ui/PageHeader";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  fetchAreas,
  fetchDocumentTypes,
  fetchOrgs,
  fetchPriorities,
  fetchSecurityTypes,
  submitText,
  updateDraftText,
  uploadSignFile,
} from "@/features/text-sign/api";
import { SignerPickerDialog, type PickedSigner, SIGN_ROLES } from "./SignerPicker";
import { SIGN_ROLE_LABEL } from "@/features/text-sign/helpers";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth/store";

/**
 * `fileId` có thể đến từ HAI bảng khác nhau, nên phải mang theo nguồn:
 *  - vừa upload xong  -> id của bảng `files`
 *  - nạp lại bản nháp -> `ATTACH_ID` của bảng `ATTACH`
 * Hai dãy id này độc lập nhau nên trùng số là chuyện bình thường; thiếu `source`
 * thì API tra nhầm bảng, nhẹ thì 404, nặng thì trả về NHẦM file của văn bản khác.
 */
type FileSource = "files" | "attach";
type UploadedFile = { fileId: number; fileName: string; size: number; source: FileSource };

const inputCls =
  "w-full h-9 px-3 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const textareaCls =
  "w-full min-h-[92px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const labelCls = "text-sm text-muted-foreground";

export function TextSignNewPage({ mode = "create" }: { mode?: "create" | "edit" } = {}) {
  const nav = useNavigate();
  const params = useParams<{ id: string }>();
  const editTextId = mode === "edit" ? Number(params.id) : undefined;
  const user = useAuthStore((s) => s.user);

  // Thông tin văn bản
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [areaId, setAreaId] = useState<number | "">("");
  const [typeId, setTypeId] = useState<number | "">("");
  const [stypeId, setStypeId] = useState<number | "">(1); // default Bình thường
  const [priorityId, setPriorityId] = useState<number | "">(1);
  const [publishOrgId, setPublishOrgId] = useState<number | "">(user?.orgId ?? "");
  const [autoPublish, setAutoPublish] = useState(false);
  const [parallelSign, setParallelSign] = useState(false);

  // File ký + File đính kèm
  const [mainFile, setMainFile] = useState<UploadedFile | null>(null);
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [convertNotice, setConvertNotice] = useState<string | null>(null);
  /** Báo đã lưu xong — trước đây bấm Ghi lại (nháp) không có phản hồi gì. */
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  // Danh sách ký
  const [signers, setSigners] = useState<PickedSigner[]>([]);
  const [signerDialogOpen, setSignerDialogOpen] = useState(false);
  const [signerDialogMode, setSignerDialogMode] = useState<"internal" | "bypass">("internal");

  // Annotation modal
  const [annotOpen, setAnnotOpen] = useState(false);
  const [annotCount, setAnnotCount] = useState<number | null>(null);

  // EDIT mode: load existing draft and populate once
  const draft = useQuery({
    queryKey: ["sign-draft", editTextId],
    queryFn: () => fetchTextSign(editTextId!),
    enabled: !!editTextId,
  });
  useEffect(() => {
    const d = draft.data;
    if (!d || mode !== "edit") return;
    setTitle(d.title ?? "");
    setDescription(d.description ?? "");
    setCode(d.code ?? "");
    setTypeId(d.typeId ?? "");
    setPriorityId(d.priorityId ?? 1);
    // Ghi chú cũ ở đây nói API không trả về Lĩnh vực / Độ mật nên gán cứng —
    // sai: detail DTO có đủ areaId và stypeId, gán cứng làm mở lại nháp là mất
    // hai trường bắt buộc này.
    setStypeId(d.stypeId ?? 1);
    setAreaId(d.areaId ?? "");
    // "Đơn vị ban hành" lưu ở publishOrgId. Trước đây đọc nhầm createGroupId
    // (đơn vị tạo văn bản) nên mở lại bản nháp là thấy sai đơn vị đã chọn.
    setPublishOrgId(d.publishOrgId ?? d.createGroupId ?? user?.orgId ?? "");
    setAutoPublish(false);
    setParallelSign(false);
    // API tách sẵn: `mainFile` là File ký (ATTACH_TYPE=1), `attachments` là tài
    // liệu đính kèm phụ. Trước đây ở đây lấy `attachments[0]` làm File ký theo
    // hợp đồng CŨ — mà API giờ trả `attachments` rỗng, nên mở lại bản nháp là
    // mất File ký đã upload.
    if (d.mainFile) {
      setMainFile({
        fileId: d.mainFile.fileId,
        fileName: d.mainFile.fileName ?? "(no name)",
        size: d.mainFile.fileSize ?? 0,
        source: "attach", // id lấy từ text_attach -> ATTACH_ID
      });
    }
    setAttachments(
      (d.attachments ?? []).map((a) => ({
        fileId: a.fileId,
        fileName: a.fileName ?? "(no name)",
        size: a.fileSize ?? 0,
        source: "attach" as const, // id lấy từ text_attach_other -> ATTACH_ID
      })),
    );
    // Existing signers from trail
    setSigners(
      (d.trail ?? []).map((t) => ({
        staffId: t.receiverId ?? 0,
        fullName: t.receiverName ?? "",
        loginName: "",
        role: t.receiverRole ?? SIGN_ROLES[0].id,
        level: t.signLevel ?? 1,
      })),
    );
  }, [draft.data, mode, user?.orgId]);

  // Refresh annotation count when editing an existing draft
  useEffect(() => {
    if (!editTextId) {
      setAnnotCount(null);
      return;
    }
    fetchAnnotations(editTextId)
      .then((a) => setAnnotCount(a.length))
      .catch(() => setAnnotCount(null));
  }, [editTextId]);

  // Lookups
  const areas = useQuery({ queryKey: ["areas"], queryFn: fetchAreas, staleTime: 5 * 60_000 });
  const types = useQuery({
    queryKey: ["doc-types"],
    queryFn: fetchDocumentTypes,
    staleTime: 5 * 60_000,
  });
  const priorities = useQuery({
    queryKey: ["priorities"],
    queryFn: fetchPriorities,
    staleTime: 5 * 60_000,
  });
  const secTypes = useQuery({
    queryKey: ["security-types"],
    queryFn: fetchSecurityTypes,
    staleTime: 5 * 60_000,
  });
  const orgs = useQuery({
    queryKey: ["orgs-all"],
    queryFn: () => fetchOrgs(),
    staleTime: 5 * 60_000,
  });

  const submit = useMutation({
    mutationFn: async (saveAsDraft: boolean) => {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        code: code.trim() || undefined,
        typeId: typeId || undefined,
        areaId: areaId || undefined,
        stypeId: stypeId || undefined,
        priorityId: priorityId || undefined,
        publishOrgId: publishOrgId || undefined,
        autoPublish,
        parallelSign,
        mainFileId: mainFile?.fileId,
        attachmentFileIds: attachments.map((a) => a.fileId),
        signers: signers.map((s) => ({
          staffId: s.staffId,
          role: s.role,
          level: s.level,
        })),
        saveAsDraft,
      };
      return editTextId ? updateDraftText(editTextId, body) : submitText(body);
    },
    onSuccess: ({ textId }, saveAsDraft) => {
      if (saveAsDraft) {
        setSavedNotice(`Đã lưu nháp lúc ${new Date().toLocaleTimeString("vi-VN")}`);
        window.setTimeout(() => setSavedNotice(null), 4000);
      }
      // Stay on edit page if still draft, otherwise navigate to detail
      if (saveAsDraft) {
        nav(`/signs/${textId}/edit`, { replace: true });
      } else {
        nav(`/signs/${textId}`);
      }
    },
  });

  const onUploadMain = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingMain(true);
    setConvertNotice(null);
    try {
      const isOffice = /\.(docx?|odt|rtf)$/i.test(f.name);
      const up = await uploadSignFile(f, { convert: isOffice });
      setMainFile({
        fileId: up.fileId,
        fileName: up.fileName ?? f.name,
        size: up.size,
        source: "files",
      });
      if (up.converted) {
        setConvertNotice(`Đã tự động chuyển "${f.name}" sang PDF: ${up.fileName}`);
      }
    } catch (err) {
      setConvertNotice(`Lỗi upload: ${(err as Error).message}`);
    } finally {
      setUploadingMain(false);
      e.target.value = "";
    }
  };

  const onUploadAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploadingAttach(true);
    try {
      const ups = await Promise.all(files.map((f) => uploadSignFile(f)));
      setAttachments((prev) => [
        ...prev,
        ...ups.map((u, i) => ({
          fileId: u.fileId,
          fileName: u.fileName ?? files[i].name,
          size: u.size,
          source: "files" as const, // vừa upload -> id của bảng files
        })),
      ]);
    } finally {
      setUploadingAttach(false);
      e.target.value = "";
    }
  };

  const isRequiredMissing =
    !title.trim() ||
    !areaId ||
    !typeId ||
    !stypeId ||
    !priorityId ||
    !publishOrgId ||
    !mainFile ||
    signers.length === 0 ||
    !code.trim() ||
    !description.trim();

  const canSubmit = !isRequiredMissing && !submit.isPending;

  return (
    <div>
      <PageHeader
        title={mode === "edit" ? "Chỉnh sửa văn bản nháp" : "Soạn văn bản / Trình ký"}
        breadcrumb={
          mode === "edit"
            ? `Văn bản Ký điện tử › Sửa nháp #${editTextId}`
            : "Văn bản Ký điện tử › Soạn"
        }
        subtitle={
          mode === "edit"
            ? "Cập nhật nội dung văn bản nháp rồi ghi lại hoặc trình ký"
            : "Khai báo thông tin văn bản, đính kèm file và xác định luồng ký"
        }
      />

      <Card>
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Thông tin văn bản</CardTitle>
          <span className="text-xs text-muted-foreground">
            Các trường có <span className="text-destructive">*</span> là bắt buộc
          </span>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* LEFT column */}
            <div className="space-y-3">
              <Field label="Tên văn bản" required>
                <input
                  className={inputCls}
                  placeholder="Nhập trích yếu nội dung văn bản"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field label="Lĩnh vực" required>
                <select
                  className={inputCls}
                  value={areaId}
                  onChange={(e) => setAreaId(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">—— Chọn ——</option>
                  {areas.data?.map((a) => (
                    <option key={a.areaId} value={a.areaId}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Thể thức" required>
                <select
                  className={inputCls}
                  value={typeId}
                  onChange={(e) => setTypeId(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">—— Chọn ——</option>
                  {types.data?.map((t) => (
                    <option key={t.typeId} value={t.typeId}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Mã văn bản" required>
                <input
                  className={inputCls}
                  placeholder="Nhập mã văn bản"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
              <Field label="File ký" required>
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-input bg-background text-sm cursor-pointer hover:bg-muted">
                    <Paperclip className="h-4 w-4" /> Chọn file
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={onUploadMain}
                    />
                  </label>
                  {uploadingMain && (
                    <span className="text-xs text-muted-foreground">Đang tải…</span>
                  )}
                  {mainFile && (
                    <div className="flex items-center gap-1.5 text-sm">
                      <FileSignature className="h-4 w-4 text-primary-600" />
                      <span className="font-medium truncate max-w-[240px]">
                        {mainFile.fileName}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({Math.round(mainFile.size / 1024)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={() => setMainFile(null)}
                        className="ml-1 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-destructive mt-1">
                  Định dạng pdf, doc, docx; dung lượng không quá 100MB. Doc/docx sẽ tự chuyển sang
                  PDF.
                </p>
                {convertNotice && <p className="text-xs text-success mt-1">{convertNotice}</p>}
              </Field>
              <Field label="File đính kèm">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-input bg-background text-sm cursor-pointer hover:bg-muted">
                    <Paperclip className="h-4 w-4" /> Chọn file
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.msg,.mpp,.txt,.jpg,.jpeg,.png,.mp3,.mp4,.rar,.zip"
                      className="hidden"
                      onChange={onUploadAttach}
                    />
                  </label>
                  {uploadingAttach && (
                    <span className="text-xs text-muted-foreground">Đang tải…</span>
                  )}
                </div>
                {attachments.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {attachments.map((a) => (
                      <li
                        key={a.fileId}
                        className="flex items-center gap-2 text-sm px-2 py-1 bg-muted/40 rounded"
                      >
                        <FileText className="h-3.5 w-3.5 text-primary-600 shrink-0" />
                        <span className="truncate flex-1">{a.fileName}</span>
                        <span className="text-xs text-muted-foreground">
                          {Math.round(a.size / 1024)} KB
                        </span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setAttachments((prev) => prev.filter((p) => p.fileId !== a.fileId))
                          }
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Định dạng doc, docx, xls, xlsx, ppt, pptx, pdf, msg, mpp, txt, jpg, jpeg, png,
                  mp3, mp4, rar, zip; dung lượng không quá 100MB mỗi file
                </p>
              </Field>
            </div>

            {/* RIGHT column */}
            <div className="space-y-3">
              <Field label="Nội dung văn bản" required>
                <textarea
                  className={textareaCls}
                  placeholder="Nhập nội dung văn bản"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
              <Field label="Độ mật" required>
                <div className="flex items-center gap-3">
                  <select
                    className={cn(inputCls, "max-w-[240px]")}
                    value={stypeId}
                    onChange={(e) => setStypeId(e.target.value ? Number(e.target.value) : "")}
                  >
                    <option value="">—— Chọn ——</option>
                    {secTypes.data?.map((s) => (
                      <option key={s.stypeId} value={s.stypeId}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-destructive">
                    Lưu ý: Chọn đúng độ mật trước khi trình
                  </span>
                </div>
              </Field>
              <Field label="Độ khẩn" required>
                <select
                  className={cn(inputCls, "max-w-[240px]")}
                  value={priorityId}
                  onChange={(e) => setPriorityId(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">—— Chọn ——</option>
                  {priorities.data?.map((p) => (
                    <option key={p.priorityId} value={p.priorityId}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Ban hành tự động">
                <div className="h-9 flex items-center">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoPublish}
                      onChange={(e) => setAutoPublish(e.target.checked)}
                      className="h-4 w-4 rounded border-input accent-primary-600"
                    />
                    <span className="text-sm">Tự ban hành sau khi văn thư xét duyệt</span>
                  </label>
                </div>
              </Field>
              <Field label="Đơn vị ban hành" required>
                <select
                  className={inputCls}
                  value={publishOrgId}
                  onChange={(e) => setPublishOrgId(e.target.value ? Number(e.target.value) : "")}
                >
                  <option value="">—— Chọn ——</option>
                  {orgs.data?.map((o) => (
                    <option key={o.orgId} value={o.orgId}>
                      {o.name ?? o.code}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="mt-4">
        <CardHeader className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle>Danh sách ký</CardTitle>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={parallelSign}
              onChange={(e) => setParallelSign(e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary-600"
            />
            Trình ký song song
          </label>
        </CardHeader>
        <CardBody>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSignerDialogMode("internal");
                setSignerDialogOpen(true);
              }}
            >
              <UserPlus className="h-4 w-4 mr-1.5" /> Bổ sung người ký nội bộ
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSignerDialogMode("bypass");
                setSignerDialogOpen(true);
              }}
            >
              <UserCheck className="h-4 w-4 mr-1.5" /> Bổ sung người ký vượt cấp
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!editTextId || !mainFile || signers.length === 0}
              onClick={() => setAnnotOpen(true)}
              title={
                !editTextId
                  ? "Bấm Ghi lại (nháp) trước để có textId"
                  : !mainFile
                    ? "Upload File ký trước"
                    : signers.length === 0
                      ? "Thêm ít nhất 1 người ký"
                      : undefined
              }
            >
              <MapPin className="h-4 w-4 mr-1.5" />
              Gắn vị trí ký
              {annotCount !== null && (
                <span className="ml-2 text-xs">
                  ({annotCount}/{signers.length})
                </span>
              )}
            </Button>
            <Button size="sm" variant="outline" disabled title="Sắp có">
              <Copy className="h-4 w-4 mr-1.5" /> Chọn mẫu trình ký
            </Button>
          </div>

          {signers.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-md">
              Chưa có người ký — bấm nút trên để thêm.
            </div>
          ) : (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="min-w-full w-max sm:w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-2 w-10">STT</th>
                    <th className="text-left px-4 py-2">Người ký</th>
                    <th className="text-left px-4 py-2">Đơn vị / Vai trò</th>
                    <th className="text-center px-4 py-2 w-20">Cấp</th>
                    <th className="text-right px-4 py-2 w-32">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {signers
                    .slice()
                    .sort((a, b) => a.level - b.level)
                    .map((s, i, arr) => (
                      <tr
                        key={`${s.staffId}-${s.level}-${i}`}
                        className="border-b border-border last:border-b-0 hover:bg-muted/30"
                      >
                        <td className="px-4 py-2 tabular-nums text-muted-foreground">{i + 1}</td>
                        <td className="px-4 py-2">
                          <div className="font-medium">{s.fullName}</div>
                          <div className="text-xs text-muted-foreground">{s.loginName}</div>
                        </td>
                        <td className="px-4 py-2 text-muted-foreground text-xs">
                          {SIGN_ROLE_LABEL[s.role] ??
                            SIGN_ROLES.find((r) => r.id === s.role)?.label ??
                            s.role}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">
                          <span className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded bg-primary-50 text-primary-700 text-xs font-semibold">
                            {s.level}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              disabled={i === 0}
                              className="h-7 w-7 grid place-items-center rounded hover:bg-muted disabled:opacity-30"
                              title="Lên cấp"
                              onClick={() => {
                                const sorted = arr;
                                const prev = sorted[i - 1];
                                if (!prev) return;
                                setSigners((all) =>
                                  all.map((x) => {
                                    if (x.staffId === s.staffId && x.level === s.level)
                                      return { ...x, level: prev.level };
                                    if (x.staffId === prev.staffId && x.level === prev.level)
                                      return { ...x, level: s.level };
                                    return x;
                                  }),
                                );
                              }}
                            >
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              disabled={i === arr.length - 1}
                              className="h-7 w-7 grid place-items-center rounded hover:bg-muted disabled:opacity-30"
                              title="Xuống cấp"
                              onClick={() => {
                                const next = arr[i + 1];
                                if (!next) return;
                                setSigners((all) =>
                                  all.map((x) => {
                                    if (x.staffId === s.staffId && x.level === s.level)
                                      return { ...x, level: next.level };
                                    if (x.staffId === next.staffId && x.level === next.level)
                                      return { ...x, level: s.level };
                                    return x;
                                  }),
                                );
                              }}
                            >
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="h-7 w-7 grid place-items-center rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                              title="Xoá"
                              onClick={() =>
                                setSigners((prev) =>
                                  prev.filter(
                                    (p) => !(p.staffId === s.staffId && p.level === s.level),
                                  ),
                                )
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="md"
          disabled={!title.trim() || submit.isPending}
          onClick={() => submit.mutate(true)}
        >
          {submit.isPending ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1.5" />
          )}
          {submit.isPending ? "Đang lưu…" : "Ghi lại (nháp)"}
        </Button>
        <Button
          variant="default"
          size="md"
          disabled={!canSubmit}
          onClick={() => submit.mutate(false)}
        >
          <Send className="h-4 w-4 mr-1.5" /> Trình ký
        </Button>
        {savedNotice && (
          <span className="text-sm text-success basis-full text-center">{savedNotice}</span>
        )}
        {submit.error && (
          <span className="text-sm text-destructive basis-full text-center">
            Lỗi: {(submit.error as Error).message}
          </span>
        )}
      </div>

      <SignerPickerDialog
        open={signerDialogOpen}
        mode={signerDialogMode}
        defaultOrgId={user?.orgId ?? undefined}
        onClose={() => setSignerDialogOpen(false)}
        onPick={(s) => setSigners((prev) => [...prev, s])}
        defaultLevel={signers.length === 0 ? 1 : Math.max(...signers.map((s) => s.level)) + 1}
      />

      <AnnotationModal
        open={annotOpen}
        textId={editTextId ?? null}
        mainFileId={mainFile?.fileId ?? null}
        mainFileSource={mainFile?.source ?? "files"}
        signers={signers.map((s) => ({
          staffId: s.staffId,
          fullName: s.fullName,
          level: s.level,
        }))}
        onClose={() => setAnnotOpen(false)}
        onSaved={(count) => setAnnotCount(count)}
      />
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:grid sm:grid-cols-[140px_1fr] sm:items-start gap-1.5 sm:gap-3 min-h-9">
      <div
        className={cn(
          labelCls,
          "sm:h-9 sm:flex sm:items-center",
          required && "after:content-['*'] after:text-destructive after:ml-1",
        )}
      >
        {label}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
